<script setup>
import {
  NO_DATA_FOUND,
  SOCIAL_LISTENING_DRAWERS,
  SOCIAL_LISTENING_TOPIC_TOOLTIPS,
  SOCIAL_LISTENING_TOPIC_TOOLTIPS_GDI,
  SOCIAL_LISTENING_TREND_TOOLTIPS_GDI,
} from '@/app/socialListening/constants';
import {
  Button,
  DataGrid,
  MediaCard,
  Section,
  SectionHeader,
  useDrawer,
  useToast,
  VisualFilterOverlay,
} from '@dashhudson/dashing-ui';
import { computed, onMounted, ref, watch } from 'vue';
import { useSocialListeningTrendsStore } from '@/stores/social-listening-trends';
import { MEDIA_TYPE_OPTIONS } from '@/app/library/constants';
import { formatPostDataToMediaCardProps } from '@/app/socialListening/utils/formatters';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';
import { useSocialListening } from '@/app/socialListening/composables/useSocialListening';
import { camelizeKeys } from 'humps';
import { handleVisualFilterOverlayClick } from '@/app/socialListening/utils/visual-filter-utils';
import { useSocialListeningTopPostsStore } from '@/stores/social-listening-top-posts';
import { useTrackingStore } from '@/stores/tracking';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';

// Composables
const { launchDrawer, currentDrawer } = useDrawer();
const { addToast } = useToast({ life: 5000 });
const { isTopicsPage, isTrendsPage, isComparisonPage } = useSocialListening();
// Stores
const trendsStore = useSocialListeningTrendsStore();
const topicsStore = useSocialListeningTopicsStore();
const comparisonStore = useSocialListeningComparisonsStore();
const topPostsStore = useSocialListeningTopPostsStore();
const trackingStore = useTrackingStore();

// Props
const props = defineProps({
  loading: {
    type: Boolean,
    default: false,
  },
  drawerUsage: {
    type: Boolean,
    default: false,
  },
  keyword: {
    type: String,
    default: null,
  },
  isTopicPreview: {
    type: Boolean,
    default: false,
  },
  topicPreviewDateLabel: {
    type: String,
    default: null,
  },
});

// Refs
const topPostsPreviewList = ref([]);

// Computed
const viewTopPostsDrawer = computed(
  () => currentDrawer?.value?.name === SOCIAL_LISTENING_DRAWERS.TOP_POST,
);

const viewTrendingKeywordsDrawer = computed(
  () => currentDrawer?.value?.name === SOCIAL_LISTENING_DRAWERS.TRENDING_KEYWORD,
);

const viewTopicCreatorDrawer = computed(
  () => currentDrawer?.value?.name === SOCIAL_LISTENING_DRAWERS.TOPIC_TEMPLATE_CREATOR,
);

const isDrawerOpen = computed(
  () => viewTrendingKeywordsDrawer.value || viewTopicCreatorDrawer.value,
);

const isReport = computed(() => trackingStore.currentRoute?.meta?.report);

const topPostsLists = computed(() => {
  if (props.isTopicPreview) {
    return topPostsStore.topicCreatorTopPostsPreview;
  }
  if (viewTrendingKeywordsDrawer.value) {
    return topPostsStore.trendsDrawerTopPostsPreview;
  }
  return topPostsStore.targetPageTopPostsPreview;
});

const topPostsLoading = computed(() => {
  if (props.isTopicPreview) {
    return topPostsStore.pending.topicCreatorTopPostsPreview;
  }

  if (viewTrendingKeywordsDrawer.value) {
    return topPostsStore.pending.trendsDrawerTopPostsPreview;
  }

  return topPostsStore.targetPagePendingTopPostsPreview || props.loading;
});

const formattedTopPostsPreview = computed(() =>
  topPostsLists.value?.map((topPost) => formatPostDataToMediaCardProps(topPost)),
);

const isEmpty = computed(
  () => formattedTopPostsPreview.value?.length === 0 && !topPostsLoading.value,
);

const postSourceIds = computed(() => {
  if (props.isTopicPreview) {
    return topPostsStore.topicCreatorTopPostsPreview.map((post) => ({ sourceId: post.sourceId }));
  }

  if (viewTrendingKeywordsDrawer.value) {
    return topPostsStore.trendsDrawerTopPostsPreview.map((post) => ({ sourceId: post.sourceId }));
  }

  return topPostsStore.targetPageTopPostsPreview.map((post) => ({ sourceId: post.sourceId }));
});

const displayedTopPosts = computed(() => {
  if (props.drawerUsage) {
    return formattedTopPostsPreview.value;
  }
  return topPostsPreviewList.value;
});

const showVisualOverlay = computed(() => isTopicsPage.value && !props.isTopicPreview);

const tooltip = computed(() => {
  if (isReport.value) {
    return null;
  }
  if (props.isTopicPreview) {
    return SOCIAL_LISTENING_TOPIC_TOOLTIPS_GDI.TOP_POST_TOPIC_PREVIEW;
  }
  if (viewTrendingKeywordsDrawer.value) {
    return SOCIAL_LISTENING_TREND_TOOLTIPS_GDI.TOP_POST;
  }
  if (isTopicsPage.value) {
    return SOCIAL_LISTENING_TOPIC_TOOLTIPS.TOP_POSTS;
  }
  if (isTrendsPage.value) {
    return SOCIAL_LISTENING_TREND_TOOLTIPS_GDI.TOP_POSTS_PAGE;
  }
  return null;
});

// Functions
function isTextPreview(data) {
  return [
    MEDIA_TYPE_OPTIONS.TEXT.value.toLowerCase(),
    MEDIA_TYPE_OPTIONS.LINK.value.toLowerCase(),
  ].includes(data.sourceType);
}

function findMedia(sourceId) {
  return topPostsLists.value.find((post) => post.sourceId === sourceId);
}

function openDrawer(source, sourceId) {
  launchDrawer({
    name: SOCIAL_LISTENING_DRAWERS.MEDIA_POST,
    propsList: postSourceIds.value,
    props: {
      source,
      sourceId,
      isTopicPreview: props.isTopicPreview,
      isTrendingKeyword: !!props.keyword,
    },
  });
}

async function handleVisualFilterClick(include, mediaItem) {
  const media = camelizeKeys(findMedia(mediaItem.sourceId));
  // force loading state
  topPostsStore.pending.topicsTopPostsPreview = true;
  topicsStore.pending.webResults = true;
  try {
    handleVisualFilterOverlayClick(include, media, false);
    await topicsStore.updateTopicVisualData();
  } catch {
    addToast({
      severity: 'error',
      summary: 'Visual Filter Update',
      message: 'Something went wrong',
    });
  } finally {
    topPostsStore.pending.topicsTopPostsPreview = false;
    topicsStore.pending.webResults = false;
  }
}

function onSeeMore() {
  const drawerProps = { name: SOCIAL_LISTENING_DRAWERS.TOP_POST };
  if (props.isTopicPreview) {
    drawerProps.props = {
      isTopicPreview: props.isTopicPreview,
      topicPreviewDateLabel: props.topicPreviewDateLabel,
      tooltip: tooltip.value,
    };
    launchDrawer(drawerProps);
    return;
  }

  if (isTopicsPage.value) {
    drawerProps.props = { ...topicsStore.drawerSerializedFilters };
  } else if (isComparisonPage.value) {
    const { keywordsAndHashtags, ...filterData } = comparisonStore.temporaryFilters;

    const selectedTopic = topicsStore.selectedTopic;
    comparisonStore.setDrawerSelectedTopic(selectedTopic);
    comparisonStore.setTempFilters({
      keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
      excludedHandles: selectedTopic.searchBody.filters.excludedHandles ?? [],
      ...filterData,
    });
    drawerProps.props = {
      ...comparisonStore.drawerSerializedFilters,
      keywordsAndHashtags: selectedTopic.searchBody.filters.keywordsAndHashtags,
      excludedHandles: selectedTopic.searchBody.filters.excludedHandles ?? [],
      topicId: selectedTopic.id,
    };
  } else {
    drawerProps.props = {
      ...trendsStore.drawerSerializedFilters,
      ...(props.keyword !== null ? { keyword: props.keyword } : {}),
    };
  }
  drawerProps.props.tooltip = tooltip.value;
  launchDrawer(drawerProps);
}

// Lifecycle
onMounted(() => {
  if (props.isTopicPreview) {
    topPostsStore.topicCreatorTopPostsPreview = [];
  } else if (props.drawerUsage && viewTrendingKeywordsDrawer.value) {
    topPostsStore.trendsDrawerTopPostsPreview = [];
  } else {
    topPostsStore.setTopPostsPreviewPending();
    topPostsStore.clearTopPostsPreview();
  }
});

// Watchers
watch(
  () => formattedTopPostsPreview.value,
  (to) => {
    if (to && !isDrawerOpen.value) {
      // show static set of posts if a drawer is open
      topPostsPreviewList.value = formattedTopPostsPreview.value;
    }
  },
);

// deep toasts from visual filter updates
watch(
  () => topicsStore.mostRecentToast,
  (to) => {
    if (to) addToast(to);
  },
);
</script>

<template>
  <Section>
    <slot name="header">
      <SectionHeader title="Top Performing Posts" :tooltip="tooltip" />
    </slot>
    <DataGrid
      class="!pt-0"
      :value="displayedTopPosts"
      :loading="topPostsLoading"
      :empty-state="{
        message: NO_DATA_FOUND,
      }"
      :num-rows="props.isTopicPreview ? 2 : 1"
      grid-layout-preset="MediaCard"
    >
      <template #item="{ data }">
        <MediaCard
          class="dash-cursor-pointer"
          v-bind="data"
          @click="openDrawer(data.source, data.sourceId)"
        >
          <template #overlay="{ active }">
            <VisualFilterOverlay
              v-if="!isTextPreview(data)"
              plus-icon="eye"
              minus-icon="eye-off"
              :show="active && showVisualOverlay"
              @click:plus.stop="handleVisualFilterClick(true, data)"
              @click:minus.stop="handleVisualFilterClick(false, data)"
            />
          </template>
        </MediaCard>
      </template>
    </DataGrid>
    <div class="dash-flex dash-flex-row !items-center !justify-center">
      <Button
        v-if="!isEmpty && !isReport"
        data-test="see-more-button"
        label="See More"
        severity="tertiary"
        :disabled="topPostsLoading || viewTopPostsDrawer"
        @click="onSeeMore"
      />
    </div>
  </Section>
</template>
